import { ExternalAdvance, Invoice, RetentionBackfill } from '../../types';
import { isDeductiblePriorInvoice } from '../../services/advanceInvoiceLogic';
import { invoiceAmountCents } from '../../services/invoiceLogic';
import { EXTERNAL_ADVANCE_ID_PREFIX } from '../../services/externalAdvances';
import { centsFromEuroInput, formatCentsPlain, roundCents } from '../../services/money';

/**
 * Reine Logik der Maske "Alt-Einbehalte erfassen" (RetentionBackfillModal).
 *
 * Getrennt von der Komponente, damit sie ohne Supabase und ohne DOM testbar
 * ist — dasselbe Muster wie customerLinkLogic neben ProjectCustomerLink.
 *
 * Fachlich (Bauplan §16.2): ausgestellte Belege sind eingefroren. Was der
 * Auftraggeber auf Belege VOR dem Feature oder auf externe Alt-Abschlaege
 * bereits einbehalten hat, steht deshalb in einer eigenen Tabelle
 * (invoice_retention_backfill) — je Vorrechnung ein Betrag, adressiert ueber
 * dieselbe Kennung wie im Abzugsblock (`sourceRef` = Beleg-Uuid bzw.
 * `ext:<id>`). Ohne diesen Eintrag hielte das kumulative Modell auf dem
 * naechsten Beleg den vollen Satz nach, und der Auftraggeber zahlte den
 * Einbehalt zum zweiten Mal.
 */

/** Eine Vorrechnung des Projekts, wie die Maske sie zeigt. */
export interface BackfillRow {
    /** advanceInvoiceId des Abzugs: Beleg-Uuid oder `ext:<id>`. */
    sourceRef: string;
    invoiceNumber: string;
    invoiceDate?: string;
    /** Bruttoforderung des Belegs (gross - prepaid) bzw. Betrag des Alt-Abschlags. */
    claimCents: number;
    /**
     * Was der Beleg selbst einbehalten hat (retention - release). `null` bei
     * externen Alt-Abschlaegen: es gibt keinen Beleg im System, also auch
     * keinen Systemeinbehalt — die Spalte zeigt "—".
     */
    systemRetentionCents: number | null;
    external: boolean;
    /** Bereits erfasster Alt-Einbehalt dieser Vorrechnung, falls vorhanden. */
    existing?: RetentionBackfill;
}

/** Was in der Maske je Zeile eingegeben wird — Text, wie getippt. */
export interface BackfillEdit {
    /** Betrag in Euro mit Komma; leer = kein Alt-Einbehalt. */
    amount: string;
    note: string;
}

export type BackfillEdits = Record<string, BackfillEdit>;

/** Was fuer eine geaenderte Zeile an den Dienst geht. */
export interface BackfillChange {
    sourceRef: string;
    entry: Omit<RetentionBackfill, 'id' | 'projectId'>;
}

/**
 * Sortierschluessel: Datum aufsteigend, fehlendes Datum zuerst (wie
 * fetchExternalAdvances mit nullsFirst), danach Nummer. Die Reihenfolge
 * entspricht dem Abzugsblock — wer die Maske neben dem Beleg liest, findet
 * die Zeilen in derselben Folge.
 */
const compareRows = (a: BackfillRow, b: BackfillRow): number => {
    const da = a.invoiceDate ?? '';
    const db = b.invoiceDate ?? '';
    if (da !== db) return da < db ? -1 : 1;
    return a.invoiceNumber.localeCompare(b.invoiceNumber, 'de');
};

/**
 * Zeilen der Maske aus Belegen, externen Alt-Abschlaegen und dem Bestand.
 *
 * Belege nur, wenn sie auf einer kumulativen Rechnung abzugsfaehig sind
 * (isDeductiblePriorInvoice) — nur fuer diese Quellen bildet
 * priorRetentionsFromDeductions einen Eintrag, nur dort wirkt ein
 * Alt-Einbehalt. Ein stornierter Beleg oder ein Entwurf stuende hier als
 * Zeile, deren Betrag nie angerechnet wuerde.
 *
 * Ein Backfill-Eintrag ohne passende Vorrechnung (Beleg spaeter storniert,
 * Alt-Abschlag geloescht) bleibt als eigene Zeile sichtbar: er zaehlt in
 * openRetentionCents weiter mit, und nur sichtbar laesst er sich loeschen.
 */
export const buildBackfillRows = (
    invoices: Invoice[],
    externals: ExternalAdvance[],
    backfill: RetentionBackfill[],
): BackfillRow[] => {
    const byRef = new Map<string, RetentionBackfill>();
    for (const b of backfill) byRef.set(b.sourceRef, b);

    const rows: BackfillRow[] = [];
    for (const inv of invoices) {
        if (!isDeductiblePriorInvoice(inv)) continue;
        rows.push({
            sourceRef: inv.id,
            invoiceNumber: inv.invoiceNumber || 'ohne Nummer',
            invoiceDate: inv.invoiceDate,
            claimCents: invoiceAmountCents(inv.totals.grossTotalCents, inv.totals.prepaidCents),
            systemRetentionCents:
                (inv.totals.retentionCents ?? 0) - (inv.totals.retentionReleaseCents ?? 0),
            external: false,
            existing: byRef.get(inv.id),
        });
        byRef.delete(inv.id);
    }
    for (const ext of externals) {
        const sourceRef = `${EXTERNAL_ADVANCE_ID_PREFIX}${ext.id}`;
        rows.push({
            sourceRef,
            invoiceNumber: ext.invoiceNumber,
            invoiceDate: ext.invoiceDate,
            claimCents: ext.grossCents,
            systemRetentionCents: null,
            external: true,
            existing: byRef.get(sourceRef),
        });
        byRef.delete(sourceRef);
    }
    // Verwaiste Eintraege: Vorrechnung nicht (mehr) da, Betrag aber gespeichert.
    for (const orphan of byRef.values()) {
        rows.push({
            sourceRef: orphan.sourceRef,
            invoiceNumber: orphan.invoiceNumber || 'ohne Nummer',
            invoiceDate: orphan.invoiceDate,
            claimCents: 0,
            systemRetentionCents: null,
            external: orphan.sourceRef.startsWith(EXTERNAL_ADVANCE_ID_PREFIX),
            existing: orphan,
        });
    }
    return rows.sort(compareRows);
};

/** "1.234,56" fuer das Eingabefeld; 0 oder nichts ist ein leeres Feld. */
export const euroTextFromCents = (cents: number | undefined): string =>
    cents ? formatCentsPlain(cents) : '';

/**
 * Eingabe nach Cent. Leer heisst 0 (kein Alt-Einbehalt), `null` heisst
 * "nicht lesbar" — der Aufrufer meldet das als Formularfehler. Negativ ist
 * ebenfalls nicht lesbar: ein negativer Alt-Einbehalt waere eine Auszahlung,
 * die der Auftraggeber nie geleistet hat.
 */
export const centsFromEuroText = (text: string): number | null => {
    if (!text.trim()) return 0;
    const cents = centsFromEuroInput(text);
    if (cents == null || cents < 0) return null;
    return cents;
};

/** Anfangsstand der Eingaben: was bereits erfasst ist. */
export const initialEdits = (rows: BackfillRow[]): BackfillEdits => {
    const edits: BackfillEdits = {};
    for (const r of rows) {
        edits[r.sourceRef] = {
            amount: euroTextFromCents(r.existing?.retentionCents),
            note: r.existing?.note ?? '',
        };
    }
    return edits;
};

/**
 * Vorschlag: Projektsatz auf die Forderung, kaufmaennisch gerundet. 0 bei
 * fehlender Forderung oder fehlendem Satz — ein Vorschlag von 0 laesst das
 * Feld leer, statt eine Null hineinzuschreiben.
 */
export const suggestBackfillCents = (claimCents: number, percent: number): number => {
    if (!Number.isFinite(percent) || percent <= 0 || claimCents <= 0) return 0;
    return roundCents((claimCents * percent) / 100);
};

/**
 * Hat die Vorrechnung im System bereits einen Einbehalt? Dann braucht sie
 * keinen Alt-Einbehalt — der Vorschlag laesst sie aus, und die Maske markiert
 * die Zeile. Extern (null) heisst: kein Systemeinbehalt moeglich.
 */
export const hasSystemRetention = (row: BackfillRow): boolean =>
    (row.systemRetentionCents ?? 0) !== 0;

/**
 * Vorschlag auf alle Zeilen OHNE Systemeinbehalt anwenden. Zeilen mit
 * Systemeinbehalt und verwaiste Eintraege (Forderung 0) bleiben unberuehrt;
 * die Notiz bleibt immer stehen.
 */
export const applySuggestion = (
    rows: BackfillRow[],
    edits: BackfillEdits,
    percent: number,
): BackfillEdits => {
    const next: BackfillEdits = { ...edits };
    for (const r of rows) {
        if (hasSystemRetention(r) || r.claimCents <= 0) continue;
        next[r.sourceRef] = {
            ...(edits[r.sourceRef] ?? { amount: '', note: '' }),
            amount: euroTextFromCents(suggestBackfillCents(r.claimCents, percent)),
        };
    }
    return next;
};

/** Zeilen, deren Betrag nicht lesbar ist — Speichern ist dann gesperrt. */
export const invalidRows = (rows: BackfillRow[], edits: BackfillEdits): BackfillRow[] =>
    rows.filter((r) => centsFromEuroText(edits[r.sourceRef]?.amount ?? '') == null);

/**
 * Was gegenueber dem Bestand geaendert wurde — nur das geht an den Dienst.
 *
 * Eine Zeile ohne Bestand und ohne Betrag ist keine Aenderung, auch wenn eine
 * Notiz getippt wurde: der Dienst loescht bei retentionCents <= 0, eine Notiz
 * allein liesse sich nicht speichern. Eine Zeile MIT Bestand und geleertem
 * Feld ist eine Aenderung mit Betrag 0 — der Dienst loescht den Eintrag.
 * Nicht lesbare Betraege werden uebersprungen; invalidRows sperrt vorher.
 */
export const changedRows = (rows: BackfillRow[], edits: BackfillEdits): BackfillChange[] => {
    const out: BackfillChange[] = [];
    for (const r of rows) {
        const edit = edits[r.sourceRef];
        if (!edit) continue;
        const cents = centsFromEuroText(edit.amount);
        if (cents == null) continue;
        const note = edit.note.trim();
        const wasCents = r.existing?.retentionCents ?? 0;
        const wasNote = r.existing?.note ?? '';
        if (cents === 0 && wasCents === 0) continue;
        if (cents === wasCents && note === wasNote) continue;
        out.push({
            sourceRef: r.sourceRef,
            entry: {
                sourceRef: r.sourceRef,
                invoiceNumber: r.invoiceNumber,
                invoiceDate: r.invoiceDate,
                retentionCents: cents,
                note: note || undefined,
            },
        });
    }
    return out;
};

/** Summe der eingegebenen Alt-Einbehalte; nicht lesbare Felder zaehlen 0. */
export const backfillTotalCents = (rows: BackfillRow[], edits: BackfillEdits): number =>
    rows.reduce((sum, r) => sum + (centsFromEuroText(edits[r.sourceRef]?.amount ?? '') ?? 0), 0);
